import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Space, Tooltip, message, Row, Col, Card, Typography, Badge, Upload } from 'antd';
import { CopyOutlined, InfoCircleOutlined, ArrowLeftOutlined, SaveOutlined, GlobalOutlined, PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import videoService from '../../services/videoService';
import templatesService from '../../services/templatesService';
import masterService from '../../services/masterService';
import ToggleSwitch from '../common/ToggleSwitch';
import { useNotify } from '../../context/NotificationContext';
import { getImageUrl } from '../../utils/commonFunctions';

const { TextArea } = Input;
const { Text, Title } = Typography;

const CollectionDetailsForm = ({ initialValues, loading, onSubmit, onBack }) => {
  const [form] = Form.useForm();
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [multilingualData, setMultilingualData] = useState([]);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingCollectionImage, setUploadingCollectionImage] = useState(false);
  const iconImageValue = Form.useWatch('icon_image', form);
  const collectionImageValue = Form.useWatch('image', form);

  // Helper function to create option objects
  const toOption = (value, label) => ({ value, label });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await videoService.getAllVideoCategory({ perPage: 100, currentPage: 1, searchText: '' });
      const list = res?.data?.data?.data || res?.data?.data || [];
      setCategoryOptions(list.map((r) => toOption(r.vc_id, r.vc_name)));
    } catch (error) {
      console.error('Error fetching categories:', error);
      notify.error({
        message: "Failed to load categories",
      });
      }
  };
  const notify = useNotify();

  // Fetch master categories
  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const res = await templatesService.getTemplates({ 
        isAll: 1, 
        status: 'active' 
      });
      const list = res?.data?.data || [];
      setTemplateOptions(list.map((r) => toOption(r.temp_id, r.temp_name)));
    } catch (error) {
      console.error('Error fetching templates:', error);
      notify.error({
        message: "Failed to load templates",
      });
    }
  };

  // Fetch languages
  const fetchLanguages = async () => {
    try {
      const params = {
        perPage: 1000,
        currentPage: 1,
        searchText: "",
        status: "Y",
      };
      const res = await masterService.getVideoLanguages(params);
      const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
      setLanguages(list);
    } catch (e) {
      notify.error({ message: "Failed to load languages" });
    }
  };

  // Load categories, templates, and languages on component mount
  useEffect(() => {
    // fetchMasterCategories();
    fetchTemplates();
    fetchCategories();
    fetchLanguages();
  }, []);

  const handleCopySlug = () => {
    const slug = form.getFieldValue('slug') || '';
    const url = `/api/collections/${slug}`;
    navigator.clipboard.writeText(url).then(() => message.success('Slug URL copied'));    
  };

  // Auto-generate slug from name if slug is empty or was auto-generated
  useEffect(() => {
    const unsub = form.watch?.((values, allValues) => {
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(true);

  const onNameChange = (e) => {
    const name = e?.target?.value || '';
    const currentSlug = form.getFieldValue('slug');
    // Only auto-generate slug if it hasn't been manually edited and is empty
    if (!slugManuallyEdited && !currentSlug) {
      form.setFieldsValue({ slug: slugify(name) });
    }
  };

  const onSlugChange = (e) => {
    setSlugManuallyEdited(true);
  };

  const slugify = (val) =>
    (val || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Keep form in sync when parent passes new initialValues (e.g., after getCollectionById)
  useEffect(() => {
    if (initialValues && form) {
      form.setFieldsValue({
        name: initialValues.name,
        slug: initialValues.slug,
        label: initialValues.label,
        heading: initialValues.heading,
        description: initialValues.description,
        icon_image: initialValues.icon_image,
        image: initialValues.image || initialValues.image,
        is_manual: !!initialValues.is_manual,
        fk_template_id: initialValues.fk_template_id || initialValues?.template_relation?.temp_id,
        fk_category_id: initialValues.fk_category_id || initialValues?.categories_relation?.vc_id,
      });
      // Load multilingual data if available, otherwise reset to empty array
      if (initialValues.multilingual_data && Array.isArray(initialValues.multilingual_data) && initialValues.multilingual_data.length > 0) {
        setMultilingualData(initialValues.multilingual_data.map((item) => ({
          ...item,
          icon_image: item.icon_image || null,
          image: item.image || null,
        })));
      } else {
        setMultilingualData([]);
      }
    } else if (!initialValues) {
      // Reset form and multilingual data when initialValues is null/undefined
      form.resetFields();
      setMultilingualData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, initialValues?.multilingual_data]);

  const uploadCollectionAsset = async (file, field, setUploading) => {
    if (!file?.type?.startsWith('image/')) {
      notify.error({ message: 'You can only upload image files!' });
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notify.error({ message: 'Image must be smaller than 10MB!' });
      return Upload.LIST_IGNORE;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image_type', 'images');
      formData.append('folder_name', 'collection');
      formData.append('image', file);
      const response = await masterService.uploadImage(formData);
      if (response?.data?.status) {
        const imagePath = response.data.data?.s3_key;
        form.setFieldsValue({ [field]: imagePath });
        notify.success({ message: 'Image uploaded successfully' });
      } else {
        notify.error({ message: 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      notify.error({ message: 'Failed to upload image' });
    } finally {
      setUploading(false);
    }

    return false;
  };

  const uploadMultilingualImage = async (file, index, field) => {
    if (!file?.type?.startsWith('image/')) {
      notify.error({ message: 'You can only upload image files!' });
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      notify.error({ message: 'Image must be smaller than 10MB!' });
      return Upload.LIST_IGNORE;
    }

    try {
      const formData = new FormData();
      formData.append('image_type', 'images');
      formData.append('folder_name', 'collection');
      formData.append('image', file);
      const response = await masterService.uploadImage(formData);
      if (response?.data?.status) {
        const imagePath = response.data.data?.s3_key;
        const updated = [...multilingualData];
        updated[index][field] = imagePath;
        setMultilingualData(updated);
        notify.success({ message: 'Image uploaded successfully' });
      } else {
        notify.error({ message: 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Multilingual image upload failed:', error);
      notify.error({ message: 'Failed to upload image' });
    }

    return false;
  };

  const handleRemoveImage = (field) => {
    form.setFieldsValue({ [field]: null });
    notify.success({ message: 'Image removed' });
  };

  const handleFormSubmit = (values) => {
    // Validate multilingual data
    const langs = multilingualData.map((d) => d.lang_id).filter(Boolean);
    if (new Set(langs).size !== langs.length) {
      notify.error({ message: "Duplicate languages in multilingual data" });
      return;
    }
    for (let i = 0; i < multilingualData.length; i++) {
      const d = multilingualData[i];
      if (d.lang_id && !d.name) {
        notify.error({
          message: `Language entry #${i + 1}: Name is required when language is selected`,
        });
        return;
      }
    }
    // Include multilingual data in the submission
    const { image, ...restValues } = values;
    const payload = {
      ...restValues,
      icon_image: values.icon_image || null,
      image: image || null,
      multilingual_data: multilingualData,
    };

    console.log("payload123", payload);
    onSubmit(payload);
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onFinish={handleFormSubmit}
      style={{ maxWidth: 1100 }}
    >
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ paddingBottom: 8 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}
              tooltip={{ title: 'This is displayed in admin and optionally UI', icon: <InfoCircleOutlined /> }}>
              <Input placeholder="e.g. Trending Now" onChange={onNameChange} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="slug" label="Slug"
              tooltip={{ title: 'URL-friendly identifier. Auto-generated from name if empty, but can be manually edited.', icon: <InfoCircleOutlined /> }}>
              <Input
                disabled={slugManuallyEdited}
                placeholder="auto from name if blank"
                onChange={onSlugChange}
                addonBefore="/api/collection/"
                addonAfter={
                  <Tooltip title="Copy full slug URL">
                    <Button type="text" icon={<CopyOutlined />} onClick={handleCopySlug} />
                  </Tooltip>
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="label" label="Label">
              <Input placeholder="Optional label" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="heading" label="Heading">
              <Input placeholder="Optional heading shown on UI" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label="Description">
              <TextArea rows={4} placeholder="Describe the collection" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Multilingual Data Section */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }} 
        bodyStyle={{ paddingBottom: 8 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GlobalOutlined />
            <Title level={5} style={{ margin: 0 }}>Multilingual Image & Data</Title>
          </div>
        }
        extra={
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() =>
              setMultilingualData([
                ...multilingualData,
                {
                  lang_id: null,
                  name: "",
                  label: "",
                  heading: "",
                  description: "",
                  image:"",
                  icon_image:''
                },
              ])
            }
          >
            Add Language
          </Button>
        }
      >
        {multilingualData.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: 24,
              background: '#fafafa',
              border: '1px dashed #d9d9d9',
              borderRadius: 8,
            }}
          >
            <Text type="secondary">
              No multilingual data added yet. Click "Add Language" to get started.
            </Text>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {multilingualData.map((data, index) => (
            <Card
              key={`ml-${index}`}
              size="small"
              type="inner"
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text strong>Language Entry #{index + 1}</Text>
                  {data.lang_id && (
                    <Badge
                      count={
                        languages.find((l) => l.lang_id === data.lang_id)
                          ?.lang_name || "Unknown Language"
                      }
                      style={{ backgroundColor: "#1890ff" }}
                    />
                  )}
                </div>
              }
              extra={
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    setMultilingualData(
                      multilingualData.filter((_, i) => i !== index)
                    )
                  }
                >
                  Remove
                </Button>
              }
            >
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Language"
                    required
                    style={{ marginBottom: 8 }}
                  >
                    <Select
                      value={data.lang_id}
                      placeholder="Select language"
                      onChange={(value) => {
                        const arr = [...multilingualData];
                        arr[index].lang_id = value;
                        setMultilingualData(arr);
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children || '')
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {languages.map((lang) => (
                        <Select.Option key={lang.lang_id} value={lang.lang_id}>
                          {lang.lang_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Name"
                    required
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      value={data.name}
                      placeholder="Enter name in this language"
                      onChange={(e) => {
                        const arr = [...multilingualData];
                        arr[index].name = e.target.value;
                        setMultilingualData(arr);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Label"
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      value={data.label}
                      placeholder="Enter label in this language"
                      onChange={(e) => {
                        const arr = [...multilingualData];
                        arr[index].label = e.target.value;
                        setMultilingualData(arr);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Heading"
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      value={data.heading}
                      placeholder="Enter heading in this language"
                      onChange={(e) => {
                        const arr = [...multilingualData];
                        arr[index].heading = e.target.value;
                        setMultilingualData(arr);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label="Description"
                    style={{ marginBottom: 8 }}
                  >
                    <TextArea
                      rows={3}
                      value={data.description}
                      placeholder="Enter description in this language"
                      onChange={(e) => {
                        const arr = [...multilingualData];
                        arr[index].description = e.target.value;
                        setMultilingualData(arr);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Icon Image" style={{ marginBottom: 8 }}>
                    {data.icon_image && (
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: 6,
                            color: '#fa8c16',
                            marginBottom: '8px',
                          }}
                        >
                          <Text strong>{getImageUrl(data.icon_image)}</Text>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fafafa',
                          }}
                        >
                          <img
                            src={getImageUrl(data.icon_image)}
                            alt="Icon preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <ImgCrop aspect={34/34} rotationSlider quality={1}>
                      <Upload
                        accept="image/*"
                        beforeUpload={(file) =>
                          uploadMultilingualImage(file, index, 'icon_image')
                        }
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          style={{ width: '100%' }}
                        >
                          {data.icon_image ? 'Change Icon Image' : 'Upload Icon Image'}
                        </Button>
                      </Upload>
                    </ImgCrop>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Recommended size: 34px × 34px
                      </Text>
                    </div>
                    {data.icon_image && (
                      <div style={{ marginTop: '8px' }}>
                        <Button
                          type="link"
                          danger
                          onClick={() => {
                            const arr = [...multilingualData];
                            arr[index].icon_image = null;
                            setMultilingualData(arr);
                            notify.success({ message: 'Image removed' });
                          }}
                          size="small"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Collection Image" style={{ marginBottom: 8 }}>
                    {data.image && (
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            padding: '8px 12px',
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: 6,
                            color: '#fa8c16',
                            marginBottom: '8px',
                          }}
                        >
                          <Text strong>{getImageUrl(data.image)}</Text>
                        </div>
                        <div
                          style={{
                            width: '100%',
                            height: '120px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fafafa',
                          }}
                        >
                          <img
                            src={getImageUrl(data.image)}
                            alt="Collection preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      </div>
                    )}
                    <ImgCrop aspect={320 / 180} rotationSlider quality={1}>
                      <Upload
                        accept="image/*"
                        beforeUpload={(file) =>
                          uploadMultilingualImage(file, index, 'image')
                        }
                        showUploadList={false}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          style={{ width: '100%' }}
                        >
                          {data.image ? 'Change Collection Image' : 'Upload Collection Image'}
                        </Button>
                      </Upload>
                    </ImgCrop>
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Recommended size: 320px × 180px
                      </Text>
                    </div>
                    {data.image && (
                      <div style={{ marginTop: '8px' }}>
                        <Button
                          type="link"
                          danger
                          onClick={() => {
                            const arr = [...multilingualData];
                            arr[index].image = null;
                            setMultilingualData(arr);
                            notify.success({ message: 'Image removed' });
                          }}
                          size="small"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ paddingBottom: 8 }}>
        <Row gutter={[16, 8]}>
       
        <Col xs={24} md={8}>
            <Form.Item name="icon_image" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item label="Icon Image" style={{ marginBottom: 0 }}>
              {iconImageValue && (
                <div style={{ marginBottom: '12px' }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      borderRadius: 6,
                      color: '#fa8c16',
                      marginBottom: '8px',
                    }}
                  >
                    <Text strong>{getImageUrl(iconImageValue)}</Text>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '120px',
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fafafa',
                    }}
                  >
                    <img
                      src={getImageUrl(iconImageValue)}
                      alt="Icon preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                </div>
              )}
              <ImgCrop aspect={34/34} rotationSlider quality={1}>
                <Upload
                  accept="image/*"
                  beforeUpload={(file) =>
                    uploadCollectionAsset(file, 'icon_image', setUploadingIcon)
                  }
                  showUploadList={false}
                  disabled={uploadingIcon}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploadingIcon}
                    disabled={uploadingIcon}
                    style={{ width: '100%' }}
                  >
                    {iconImageValue ? 'Change Icon Image' : 'Upload Icon Image'}
                  </Button>
                </Upload>
              </ImgCrop>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Recommended size: 34px × 34px
                </Text>
              </div>
              {iconImageValue && (
                <div style={{ marginTop: '8px' }}>
                  <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveImage('icon_image')}
                    size="small"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="image" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item label="Collection Image" style={{ marginBottom: 0 }}>
              {collectionImageValue && (
                <div style={{ marginBottom: '12px' }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      borderRadius: 6,
                      color: '#fa8c16',
                      marginBottom: '8px',
                    }}
                  >
                    <Text strong>{getImageUrl(collectionImageValue)}</Text>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '120px',
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fafafa',
                    }}
                  >
                    <img
                      src={getImageUrl(collectionImageValue)}
                      alt="Collection preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                </div>
              )}
              <ImgCrop aspect={320 / 180} rotationSlider quality={1}>
                <Upload
                  accept="image/*"
                  beforeUpload={(file) =>
                    uploadCollectionAsset(file, 'image', setUploadingCollectionImage)
                  }
                  showUploadList={false}
                  disabled={uploadingCollectionImage}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploadingCollectionImage}
                    disabled={uploadingCollectionImage}
                    style={{ width: '100%' }}
                  >
                    {collectionImageValue
                      ? 'Change Collection Image'
                      : 'Upload Collection Image'}
                  </Button>
                </Upload>
              </ImgCrop>
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Recommended size: 320px × 180px
                </Text>
              </div>
              {collectionImageValue && (
                <div style={{ marginTop: '8px' }}>
                  <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveImage('image')}
                    size="small"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Row gutter={[16, 8]}>

              <Col span={24}>
                <Form.Item name="fk_template_id" label="Template">
                  <Select 
                    allowClear 
                    showSearch 
                    placeholder="Select template" 
                    options={templateOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="fk_category_id" label="Category">
                  <Select 
                    allowClear 
                    showSearch 
                    placeholder="Select category" 
                    options={categoryOptions}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="is_manual" label="Manual Mode"
                  tooltip={{ title: 'If enabled, items are manually curated (Pinned tab).', icon: <InfoCircleOutlined /> }}
                >
                  <ToggleSwitch />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Sticky Action Bar */}
      <div style={{
        position: 'sticky', bottom: 0, background: '#fff', padding: '8px 0',
        borderTop: '1px solid #f0f0f0', marginTop: 8, zIndex: 1
      }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>Save</Button>
          {onBack && <Button onClick={onBack} icon={<ArrowLeftOutlined />}>Back</Button>}
        </Space>
      </div>
    </Form>
  );
};

export default CollectionDetailsForm;
